import { NextRequest, NextResponse } from 'next/server';
import { Middleware } from '@sitecore-content-sdk/nextjs/middleware';

//Key used to pass data in cookie
const ALERT_BANNER_DISMISSED_SYMBOL = 'alert-banner-dismissed';
// Key for storing array of dismissed banner IDs
const ALERT_BANNER_DISMISSED_IDS = 'alert-banner-dismissed-ids';
// Prefix for url rewrite
const ALERT_BANNER_DISMISSED_PREFIX = `_${ALERT_BANNER_DISMISSED_SYMBOL}_`;

// Creates the rewritten path including the dismissed banner IDs
export function getAlertBannerDismissedRewrite(pathname: string, dismissedIds: string): string {
  const path = pathname.startsWith('/') ? pathname : '/' + pathname;
  // Format: /_alert-banner-dismissed_{id1,id2,id3}/path
  return `/${ALERT_BANNER_DISMISSED_PREFIX}${dismissedIds}${path}`;
}

// Gets the dismissed banner IDs from the rewritten path
export function getAlertBannerDismissedFromRewrite(pathname: string): string[] {
  const path = pathname.endsWith('/') ? pathname : pathname + '/';
  const result = path.match(`${ALERT_BANNER_DISMISSED_PREFIX}(.*?)\\/`);
  if (result && result[1] !== '') {
    // Split by comma to get array of IDs
    return result[1].split(',').filter((id) => id !== '');
  }
  return [];
}

// Gets the path without the alert banner dismissed state
export function normalizeAlertBannerDismissedRewrite(pathname: string): string {
  const result = pathname.match(`${ALERT_BANNER_DISMISSED_PREFIX}.*?(?:\\/|$)`);
  return result === null ? pathname : pathname.replace(result[0], '');
}

class AlertBannerDismissedMiddleware implements Middleware {
  order = 2;

  getDismissedBannerIds(req: NextRequest): string[] {
    // Get the comma-separated list of dismissed banner IDs from cookie
    const dismissedIdsStr = req.cookies.get(ALERT_BANNER_DISMISSED_IDS)?.value;
    if (!dismissedIdsStr) {
      return [];
    }

    // Decode and split by comma
    const decodedIds = decodeURIComponent(dismissedIdsStr);
    return decodedIds.split(',').filter((id) => id !== '');
  }

  /**
   * Do the rewrite
   * @param req<NextRequest>
   * @returns Promise<NextResponse>
   */
  async handle(req: NextRequest, res?: NextResponse): Promise<NextResponse> {
    // Response will be provided if other middleware is run before us
    let response = res || NextResponse.next();

    // Don't modify redirect responses from earlier middleware (e.g. auth)
    if (res?.status && res.status >= 300 && res.status < 400) {
      return res;
    }

    const pathname = req.nextUrl.pathname;
    const basePath = res?.headers.get('x-sc-rewrite') || pathname;

    const dismissedIds = this.getDismissedBannerIds(req);

    // Only rewrite if there are dismissed banners
    if (dismissedIds.length === 0) {
      return response;
    }

    const dismissedIdsStr = dismissedIds.join(',');
    const rewritePath = getAlertBannerDismissedRewrite(basePath, dismissedIdsStr);

    const rewriteUrl = req.nextUrl.clone();
    rewriteUrl.pathname = rewritePath;

    response = NextResponse.rewrite(rewriteUrl, res);
    response.headers.set('x-sc-rewrite', rewritePath);

    return response;
  }
}

export const alertBannerDismissedMiddleware = new AlertBannerDismissedMiddleware();
